import * as Sentry from '@sentry/react'
import Page from 'components/layout/Page'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Router } from 'react-router-dom'
import history from 'router/history'
import { useAccount } from 'state/Account'
import { getUser, isAuthenticated } from 'state/Auth'
import { prefersPush, pushOn, setupDonkeyRefreshes } from 'state/Push'
import { runPendingUpdates } from 'state/Request'
import { getHasReseller, getIsCMO, useReseller } from 'state/Reseller'
import { setupRestaurantRefreshes } from 'state/Restaurants'
import { useSettings } from 'state/Settings'
import {
	getSoundEl, Locale, platform, PumpkinTimer, readFile, saveFile, useDispatchEffect, usePlainEffect, WakeLock,
} from 'utils'

Locale.configure()

export default function App() {
	//so every time the main render() is re-called, the main App is refreshed - mainly for hot-reloading, below
	const Routes = require('./router').default

	//eslint-disable-next-line no-unused-vars
	const i18n = useTranslation() //for lack of a better way to force the rest to load only after i18next is done

	//I know it doesn't make sense request these details without first checking for authentication, but... hooks.
	const settings    = useSettings()
	const isAuth      = useSelector(isAuthenticated)
	const user        = useSelector(getUser)
	const hasReseller = useSelector(getHasReseller)
	const isCMO       = useSelector(getIsCMO)
	const reseller    = useReseller()
	const account     = useAccount()

	if (isAuth) {
		new WakeLock().toggle(settings.caffeine)

		//region: Sentry identification of the current user
		// noinspection JSCheckFunctionSignatures
		Sentry.setUser({ //IP and segment were set at the start, since these are standard
			id      : `${account.id_string} (${user.username})`, //not an ID but what Sentry shows first, so...
			rep_id  : user.id,
			username: user.username,
			// email     : user.email, //unneeded since we hardly ever email users directly, let alone from Sentry
			//these are non-standard, extra fields
			account_id: account.id_string,
			reseller  : hasReseller? (reseller?.display_name || 'unsure') : 'no', //hasReseller could be safety-set as true
			// segment   : '', //doesn't seem to work, thus we're using user.reseller and app.flavor (See Sentry.inc.js)
		})
		Sentry.setTags({
			reseller: hasReseller? (isCMO? 'CMO' : 'yes') : 'no',
		})
		//endregion
	} else {
		Sentry.setUser({ authenticated: false })
	}

	usePlainEffect(PumpkinTimer.schedule)

	//registers push/refreshes: either on startup, settings change, or app was rebuilt, and we somehow lost the push handlers
	const usePush = useSelector(prefersPush)
	useDispatchEffect(isAuth? (usePush? pushOn : setupDonkeyRefreshes) : null, [isAuth, usePush])

	useDispatchEffect(setupRestaurantRefreshes, [isAuth])

	//force any refresh needed after migrations
	useDispatchEffect(isAuth? runPendingUpdates : null, [isAuth])

	//embeds the sound element in DOM, so it doesn't need to be tied to smaller components / inner JS files
	usePlainEffect(() => isAuth? getSoundEl.init(settings) : null, [isAuth, settings])

	//TODO remove this block once all Androids have hit 2.4.0
	usePlainEffect(() => {
		let isNative      = platform.isNative()
		let isAndroid     = platform.isAndroid()
		let atLeastV2_4_0 = platform.nativeVersionAtLeast('2.4.0')
		if (!isAuth || !isNative || !isAndroid || atLeastV2_4_0) {
			//nothing to save if the user isn't authenticated; it can be ignored since iOS won't migrate to HTTPS; should skip backup from 2.4.0 since that's when the migration happens
			console.log('Nothing to backup here!')
			return
		}

		//backup the current state from localStorage to the Cordova filesystem - see #48185
		const writeBackup = () => {
			const contents = window.localStorage['persist:root']
			saveFile(
				'localstorage.json',
				new Blob([contents], { type: 'application/json' }),
				file => {
					readFile(
						file.name,
						contents => {
							if (contents.length) {
								console.info(`Backup written! ${Math.round(contents.length/1024*100)/100} KiB`)
							} else {
								console.error('Whoops, it seems we wrote 0b of data to the backup! Weird.')
							}
						},
						null,
						false
					)
				},
				false
			)

			setTimeout(writeBackup, 10 * 60_000) //schedule itself to be run again in 10 minutes
		}

		writeBackup() //finally, triggers the first auto-rescheduling backup
	})

	return <>
		<Helmet
			titleTemplate={`%s - ${process.env.REACT_APP_TITLE}`}
			defaultTitle={`${process.env.REACT_APP_TITLE}`}/>
		<Router history={history}>
			<Page routes={<Routes/>}/>
		</Router>
	</>
}
